import { type ReactNode, useState } from "react"

/**
 * OKS-506 — `<img>` that degrades to a caller-supplied fallback when the
 * source is null OR fails to load.
 *
 * Both brand-facing TikTok surfaces (submission-review Sheet,
 * Content Gallery) persist a write-once snapshot of the post's
 * `cover_image_url` — a signed CDN URL with a ~6h TTL. After expiry the
 * URL 404s and the bare `<img>` rendered a broken-image glyph, because the
 * old fallback fired only when the URL was `null`, never on a load error.
 *
 * This primitive widens the fallback trigger from "null" to
 * "null OR `onError`". The error state is local and per-mount: once a cover
 * errors, the fallback stays for that mount (no retry loop, no flicker); a
 * fresh page load re-attempts the URL (it may still be within TTL).
 *
 * Renders ONE of two bare nodes — the `<img>` or the `fallback` — with NO
 * wrapping element. A wrapper would break the panel's flex row and the
 * gallery's `absolute inset-0` positioning, so each caller positions the
 * `<img>` (via `className`) and its `fallback` itself.
 */
interface ThumbnailWithFallbackProps {
  /** Snapshot cover URL; `null` renders the fallback immediately. */
  src: string | null
  /** Applied verbatim to the rendered `<img>` (caller owns positioning). */
  alt: string
  className?: string
  /** Surface-specific fallback visual (e.g. muted square / gradient tile). */
  fallback: ReactNode
  loading?: "lazy" | "eager"
}

export function ThumbnailWithFallback({
  src,
  alt,
  className,
  fallback,
  loading = "lazy",
}: ThumbnailWithFallbackProps) {
  const [hasError, setHasError] = useState(false)

  if (src == null || hasError) {
    return <>{fallback}</>
  }

  return (
    <img
      src={src}
      alt={alt}
      className={className}
      loading={loading}
      onError={() => setHasError(true)}
    />
  )
}
